import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { apiErrorText } from "@/lib/apiError";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import RegistryFieldEditor from "@/components/uni-portal/RegistryFieldEditor";
import BulkVerifyWizard from "@/components/uni-portal/BulkVerifyWizard";
import CsvUploader from "@/components/app/CsvUploader";
import EmptyCellHelpLink from "@/components/app/EmptyCellHelpLink";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Save,
  ShieldAlert,
  ShieldCheck,
  Wand2,
} from "lucide-react";

/**
 * /uni-portal/degree/:id — degree-level edit form.
 * Fields mirror the admin `/admin/verify/:id` researcher form so uni
 * owners can correct tuition, offer rate, English tests and per-
 * curriculum entry requirements.
 */
const NUMERIC_FIELDS = [
  { key: "duration_years", label: "Duration (years)", step: 0.5, min: 1, max: 10 },
  { key: "tuition_international_usd", label: "Intl tuition (USD/yr)" },
  { key: "offer_rate", label: "Offer rate (0–1)", step: 0.01, max: 1 },
  { key: "ielts_typical", label: "IELTS typical (0–9)", step: 0.5, max: 9 },
  { key: "ielts_min", label: "IELTS minimum (0–9)", step: 0.5, max: 9 },
  { key: "toefl_typical", label: "TOEFL typical (0–120)", max: 120 },
  { key: "toefl_min", label: "TOEFL minimum (0–120)", max: 120 },
];

const CURRICULUM_KEYS = ["a_level", "ib", "gpa", "cbse", "atar", "suneung", "american_diploma"];

export default function UniPortalDegreeEdit() {
  const { id } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  const [state, setState] = useState({ loading: true, degree: null, uni: null, forbidden: false });
  const [form, setForm] = useState({ official_title: "" });
  const [flags, setFlags] = useState({ interview_required: false, portfolio_required: false });
  const [entries, setEntries] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    if (authLoading || !user) {
      setState((s) => ({ ...s, loading: authLoading }));
      return;
    }
    (async () => {
      try {
        const { data: d } = await api.get(`/degrees/${id}`);
        // Fetch the uni to display its name + confirm ownership via a
        // portal-scoped list call.
        const { data: u } = await api.get(`/universities/${d.university_id}`).catch(() => ({ data: null }));
        // Owner-check: the list endpoint 403s non-owners, so if this
        // succeeds we're good.
        await api.get(`/uni-portal/degrees/${d.university_id}`);
        setState({ loading: false, degree: d, uni: u, forbidden: false });

        // Pre-populate EVERY editable field from the loaded doc so
        // owners never see a blank form for data that already exists.
        const cost = d.cost || {};
        const eng = d.english_test || {};
        const adm = d.admissions || {};
        setForm({
          official_title: d.official_title || "",
          duration_years: d.duration_years ?? "",
          tuition_international_usd: d.tuition_international_usd ?? cost.tuition_intl_annual_usd ?? "",
          tuition_domestic_annual_gbp: cost.tuition_domestic_annual_gbp ?? "",
          fee_year_label: cost.fee_year_label || "",
          tuition_domestic_note: cost.tuition_domestic_note || "",
          campus: d.campus || "",
          qualification: d.qualification || "",
          offer_rate: adm.offer_rate ?? d.offer_rate ?? "",
          ielts_typical: eng.ielts_typical ?? "",
          ielts_min: eng.ielts_min ?? "",
          toefl_typical: eng.toefl_typical ?? "",
          toefl_min: eng.toefl_min ?? "",
        });
        setFlags({
          interview_required: !!d.interview_required,
          portfolio_required: !!d.portfolio_required,
        });
        const er = d.entry_requirements || {};
        const initEntries = {};
        for (const k of CURRICULUM_KEYS) {
          const v = er[k] || {};
          initEntries[k] = {
            typical: v.typical || "",
            minimum: v.minimum || v.min || "",
            min_maths: v.min_maths || "",
            min_english: v.min_english || "",
            required_subjects: (v.required_subjects || []).join(", "),
            note: v.note || "",
          };
        }
        setEntries(initEntries);
      } catch (err) {
        const st = err?.response?.status;
        setState({ loading: false, degree: null, uni: null, forbidden: st === 401 || st === 403 });
      }
    })();
  }, [id, user, authLoading]);

  const saveField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const patch = { correction_note: note || undefined };

    for (const f of NUMERIC_FIELDS) {
      const v = form[f.key];
      if (v === "" || v === null || v === undefined) continue;
      const num = Number(v);
      if (!Number.isNaN(num)) patch[f.key] = num;
    }
    if (form.official_title && form.official_title.trim()) {
      patch.official_title = form.official_title.trim();
    }
    if (form.campus && form.campus.trim()) patch.campus = form.campus.trim();
    if (form.qualification && form.qualification.trim()) patch.qualification = form.qualification.trim();
    if (form.tuition_domestic_annual_gbp !== "" && !Number.isNaN(Number(form.tuition_domestic_annual_gbp))) {
      patch.tuition_domestic_annual_gbp = Number(form.tuition_domestic_annual_gbp);
    }
    if (form.fee_year_label && form.fee_year_label.trim()) patch.fee_year_label = form.fee_year_label.trim();
    if (form.tuition_domestic_note && form.tuition_domestic_note.trim()) patch.tuition_domestic_note = form.tuition_domestic_note.trim();
    patch.interview_required = flags.interview_required;
    patch.portfolio_required = flags.portfolio_required;

    // Entry requirements: only include curricula that have any value set.
    const er = {};
    for (const k of CURRICULUM_KEYS) {
      const v = entries[k] || {};
      const cur = {};
      for (const [key, val] of Object.entries(v)) {
        if (val === "" || val === null || val === undefined) continue;
        if (key === "required_subjects") {
          const subs = String(val).split(",").map((s) => s.trim()).filter(Boolean);
          if (subs.length > 0) cur[key] = subs;
        } else {
          cur[key] = val;
        }
      }
      if (Object.keys(cur).length > 0) er[k] = cur;
    }
    if (Object.keys(er).length > 0) patch.entry_requirements = er;

    try {
      const { data } = await api.patch(`/uni-portal/degree/${id}`, patch);
      toast.success(`Saved — ${data.patched_fields.length} field(s) updated`);
      setNote("");
    } catch (err) {
      toast.error(apiErrorText(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm" data-testid="uni-portal-degree-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="uni-portal-degree-signin">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Sign in required</h1>
          <Button className="mt-4" onClick={login}>Sign in</Button>
        </div>
      </div>
    );
  }

  if (state.forbidden || !state.degree) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="uni-portal-degree-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">
            You don't own this degree's university
          </h1>
          <p className="mt-2 text-stone-600 text-sm">
            Ask your university's Which University Advisor owner to add
            you, or claim the university first.
          </p>
        </div>
      </div>
    );
  }

  const { degree, uni } = state;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="uni-portal-degree-page">
      <Link to={`/uni-portal/uni/${degree.university_id}`} className="inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-900">
        <ArrowLeft className="h-3 w-3" /> {uni?.name || "University"}
      </Link>

      <div className="mt-3 flex items-baseline gap-3">
        <ShieldCheck className="h-4 w-4 text-emerald-700" />
        <h1 className="font-editorial text-3xl">{degree.official_title}</h1>
      </div>
      <div className="mt-1 text-xs text-stone-500 font-mono">
        {degree.qualification} · {degree.campus || "main campus"}
      </div>

      <div
        className="mb-4 p-4 border border-emerald-200 rounded-xl bg-emerald-50/60 flex items-center justify-between gap-3 flex-wrap"
        data-testid="uni-portal-degree-wizard-cta"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Wand2 className="h-4 w-4 text-emerald-800 shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-stone-900">
              Verify multiple factual fields at once
            </div>
            <div className="text-[11px] text-stone-500 leading-snug">
              Step through every empty registry field one at a time.
              Skip anything you can't answer — every save updates the
              audit trail immediately.
            </div>
          </div>
        </div>
        <Button
          onClick={() => setWizardOpen(true)}
          data-testid="uni-portal-degree-wizard-open"
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          <Wand2 className="h-4 w-4 mr-1" />
          Start bulk verify
        </Button>
      </div>

      <BulkVerifyWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        docType="degrees"
        docId={degree.id}
        doc={degree}
      />

      <RegistryFieldEditor docType="degrees" docId={degree.id} doc={degree} />

      <div className="mt-8 p-6 border border-stone-300 rounded-xl bg-white space-y-4">
        <h2 className="font-editorial text-xl">Course details</h2>
        <div>
          <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
            Official title
          </label>
          <Input
            className="mt-1"
            value={form.official_title}
            onChange={(e) => saveField("official_title", e.target.value)}
            data-testid="uni-portal-degree-title-input"
            maxLength={250}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Qualification (e.g. BA (Hons))
            </label>
            <Input
              className="mt-1"
              value={form.qualification ?? ""}
              onChange={(e) => saveField("qualification", e.target.value)}
              data-testid="uni-portal-degree-qualification-input"
              maxLength={80}
            />
          </div>
          <div>
            <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Campus
            </label>
            <Input
              className="mt-1"
              value={form.campus ?? ""}
              onChange={(e) => saveField("campus", e.target.value)}
              data-testid="uni-portal-degree-campus-input"
              maxLength={120}
              placeholder="main campus"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {NUMERIC_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
                {f.label}
              </label>
              <Input
                className="mt-1"
                type="number"
                step={f.step}
                min={f.min}
                max={f.max}
                value={form[f.key] ?? ""}
                onChange={(e) => saveField(f.key, e.target.value)}
                data-testid={`uni-portal-degree-input-${f.key}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <Switch
              checked={flags.interview_required}
              onCheckedChange={(v) => setFlags((s) => ({ ...s, interview_required: v }))}
              data-testid="uni-portal-degree-interview-switch"
            />
            Interview required
          </label>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <Switch
              checked={flags.portfolio_required}
              onCheckedChange={(v) => setFlags((s) => ({ ...s, portfolio_required: v }))}
              data-testid="uni-portal-degree-portfolio-switch"
            />
            Portfolio required
          </label>
        </div>
      </div>

      <div className="mt-6 p-6 border border-stone-300 rounded-xl bg-white space-y-4">
        <h2 className="font-editorial text-xl">Fees</h2>
        <p className="text-xs text-stone-500 -mt-3">
          Domestic fee, fee year label, and any published note. Shown on the public course page.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Domestic tuition (GBP/yr)
            </label>
            <Input
              className="mt-1"
              type="number"
              value={form.tuition_domestic_annual_gbp ?? ""}
              onChange={(e) => saveField("tuition_domestic_annual_gbp", e.target.value)}
              data-testid="uni-portal-degree-input-tuition_domestic_gbp"
            />
          </div>
          <div>
            <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Fee year (e.g. 2027/28)
            </label>
            <Input
              className="mt-1"
              value={form.fee_year_label ?? ""}
              onChange={(e) => saveField("fee_year_label", e.target.value)}
              data-testid="uni-portal-degree-input-fee_year"
              maxLength={20}
            />
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Fee note (public — displayed under the fee)
            </label>
            <Textarea
              className="mt-1"
              rows={2}
              value={form.tuition_domestic_note ?? ""}
              onChange={(e) => saveField("tuition_domestic_note", e.target.value)}
              data-testid="uni-portal-degree-input-fee_note"
              maxLength={500}
            />
          </div>
        </div>
      </div>

      <div
        className="mt-6 p-6 border border-stone-300 rounded-xl bg-stone-50 space-y-4"
        data-testid="uni-portal-degree-staff-only-narrative"
      >
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-stone-500" />
          <h2 className="font-editorial text-xl">Narrative — staff-managed</h2>
        </div>
        <p className="text-xs text-stone-500 -mt-2">
          The six sections on your public course overview are curated by
          WUA editorial staff (Feb 2026 policy). Universities cannot edit
          marketing / narrative copy directly — but you can flag anything
          that reads wrong and we'll action it in the audit trail.
        </p>
        {[
          { key: "summary", label: "Summary — the top-of-page overview" },
          { key: "admissions", label: "Admissions — offer rate, interviews, selectivity" },
          { key: "cost", label: "Cost — tuition, financial support, total cost picture" },
          { key: "curriculum", label: "Curriculum — modules, assessment, structure" },
          { key: "outcomes", label: "Outcomes — employment and earnings" },
          { key: "international", label: "International — visas, work rights, sponsorship" },
        ].map((s) => {
          const current = degree?.ai_interpretation?.[s.key];
          const prov = (degree?.ai_interpretation?.section_provenance || {})[s.key];
          return (
            <div
              key={s.key}
              data-testid={`uni-portal-degree-staff-only-${s.key}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
                  {s.label}
                </label>
                <div className="flex items-center gap-3">
                  <div className="text-[10px] font-mono text-stone-500" data-testid={`ai-provenance-${s.key}`}>
                    {prov?.edited_by ? (
                      <span className="text-emerald-700">
                        Edited by {prov.edited_by}
                        {prov.edited_at ? ` · ${new Date(prov.edited_at).toLocaleDateString()}` : ""}
                      </span>
                    ) : degree?.ai_interpretation?.model ? (
                      <span>
                        AI draft ({(degree.ai_interpretation.model || "").split(":").pop()})
                        {degree.ai_interpretation.generated_at
                          ? ` · ${new Date(degree.ai_interpretation.generated_at).toLocaleDateString()}`
                          : ""}
                      </span>
                    ) : null}
                  </div>
                  <EmptyCellHelpLink
                    degreeId={degree.id}
                    universityId={degree.university_id}
                    section="narrative"
                    field={`${s.key.charAt(0).toUpperCase()}${s.key.slice(1)} narrative`}
                    label="request an edit"
                  />
                </div>
              </div>
              <div
                className="mt-1 p-3 border border-stone-200 rounded-md bg-white text-sm text-stone-700 whitespace-pre-wrap max-h-48 overflow-y-auto"
                data-testid={`uni-portal-degree-staff-only-value-${s.key}`}
              >
                {current ? String(current) : (
                  <span className="italic text-stone-400">— not set —</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-6 border border-stone-300 rounded-xl bg-white space-y-5">
        <h2 className="font-editorial text-xl">Entry requirements by curriculum</h2>
        <p className="text-xs text-stone-500 -mt-3">
          Fill only the curricula you publish. Leave the rest blank.
        </p>
        {CURRICULUM_KEYS.map((k) => (
          <div key={k} className="border-t border-stone-200 pt-4">
            <div className="text-[11px] font-underground-sc tracking-widest text-stone-500">
              {k.replace(/_/g, " ")}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
              {["typical", "minimum", "min_maths", "min_english"].map((sub) => (
                <div key={sub}>
                  <label className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                    {sub}
                  </label>
                  <Input
                    className="mt-1 font-mono"
                    value={entries[k]?.[sub] || ""}
                    onChange={(e) =>
                      setEntries((s) => ({
                        ...s,
                        [k]: { ...(s[k] || {}), [sub]: e.target.value },
                      }))
                    }
                    data-testid={`uni-portal-entry-${k}-${sub}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                  note
                </label>
                <Input
                  className="mt-1"
                  value={entries[k]?.note || ""}
                  onChange={(e) =>
                    setEntries((s) => ({
                      ...s,
                      [k]: { ...(s[k] || {}), note: e.target.value },
                    }))
                  }
                  data-testid={`uni-portal-entry-${k}-note`}
                />
              </div>
              <div className="col-span-2 md:col-span-5">
                <label className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                  Required subjects (comma-separated, e.g. Mathematics, Chemistry)
                </label>
                <Input
                  className="mt-1"
                  value={entries[k]?.required_subjects || ""}
                  onChange={(e) =>
                    setEntries((s) => ({
                      ...s,
                      [k]: { ...(s[k] || {}), required_subjects: e.target.value },
                    }))
                  }
                  data-testid={`uni-portal-entry-${k}-required_subjects`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-6 border border-stone-300 rounded-xl bg-white">
        <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
          Correction note (private — for editors)
        </label>
        <Textarea
          className="mt-1"
          rows={2}
          placeholder="e.g. Updated per admissions handbook 2027"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="uni-portal-degree-note-input"
          maxLength={1000}
        />
        <Button
          className="mt-4"
          onClick={save}
          disabled={saving}
          data-testid="uni-portal-degree-save-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="mt-6">
        <CsvUploader
          scope="degree"
          id={id}
          onCommitted={async () => {
            try {
              const { data: d } = await api.get(`/degrees/${id}`);
              setState((s) => ({ ...s, degree: d }));
              setForm((f) => ({ ...f, official_title: d.official_title || "" }));
              setFlags({
                interview_required: !!d.interview_required,
                portfolio_required: !!d.portfolio_required,
              });
            } catch (err) {
              toast.error(apiErrorText(err, "Failed to refresh course"));
            }
          }}
        />
      </div>
    </div>
  );
}
